import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Plus,
  Loader2,
  X,
  GripVertical,
  EyeOff,
  Clock,
  RotateCcw,
  ChevronLeft,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import {
  ContactDetail,
  ContactCategory,
  ContactPriority,
  ContactInput,
  ContactCategoryInput,
  ContactPriorityInput,
  LabeledValue,
} from '@/lib/api';
import { EMAIL_LABELS, PHONE_LABELS, PRESET_COLORS, HistoryEntry, FormSnapshot } from '../types';
import { ContactAttachments } from './ContactAttachments';
import { LabeledEntryList } from './LabeledEntryList';

export function ContactFormDialog({
  open,
  onOpenChange,
  contact,
  categories,
  priorities,
  onSave,
  onCreateCategory,
  onCreatePriority,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactDetail | null;
  categories: ContactCategory[];
  priorities: ContactPriority[];
  onSave: (data: ContactInput, contactId?: string) => Promise<void>;
  onCreateCategory: (data: ContactCategoryInput) => Promise<ContactCategory>;
  onCreatePriority: (data: ContactPriorityInput) => Promise<ContactPriority>;
}) {
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#6366f1');
  const [savingCategory, setSavingCategory] = useState(false);
  const [creatingPriority, setCreatingPriority] = useState(false);
  const [newPriorityName, setNewPriorityName] = useState('');
  const [newPriorityColor, setNewPriorityColor] = useState('#6b7280');
  const [savingPriority, setSavingPriority] = useState(false);
  const [emails, setEmails] = useState<LabeledValue[]>([]);
  const [phones, setPhones] = useState<LabeledValue[]>([]);
  const [company, setCompany] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [source, setSource] = useState('');
  const [categoryId, setCategoryId] = useState<string>('none');
  const [priorityId, setPriorityId] = useState<string>('none');
  const [gatheredAt, setGatheredAt] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [newHistoryText, setNewHistoryText] = useState('');
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // Snapshot of initial values for change detection / revert
  const [initialSnapshot, setInitialSnapshot] = useState<FormSnapshot | null>(null);

  const hasChanges = useMemo(() => {
    if (!initialSnapshot || !contact) return false;
    if (
      firstName !== initialSnapshot.firstName ||
      lastName !== initialSnapshot.lastName ||
      company !== initialSnapshot.company ||
      title !== initialSnapshot.title ||
      notes !== initialSnapshot.notes ||
      source !== initialSnapshot.source ||
      categoryId !== initialSnapshot.categoryId ||
      priorityId !== initialSnapshot.priorityId ||
      gatheredAt !== initialSnapshot.gatheredAt
    )
      return true;
    // Deep compare arrays of labeled values
    const labeledEqual = (a: LabeledValue[], b: LabeledValue[]) =>
      a.length === b.length && a.every((v, i) => v.value === b[i].value && v.label === b[i].label);
    if (!labeledEqual(emails, initialSnapshot.emails)) return true;
    if (!labeledEqual(phones, initialSnapshot.phones)) return true;
    // Tags
    if (
      tags.length !== initialSnapshot.tags.length ||
      tags.some((t, i) => t !== initialSnapshot.tags[i])
    )
      return true;
    // History entries
    if (
      historyEntries.length !== initialSnapshot.historyEntries.length ||
      historyEntries.some(
        (h, i) =>
          h.text !== initialSnapshot.historyEntries[i].text ||
          h.date !== initialSnapshot.historyEntries[i].date ||
          h.showDate !== initialSnapshot.historyEntries[i].showDate,
      )
    )
      return true;
    return false;
  }, [
    initialSnapshot,
    contact,
    firstName,
    lastName,
    emails,
    phones,
    company,
    title,
    notes,
    source,
    categoryId,
    priorityId,
    gatheredAt,
    tags,
    historyEntries,
  ]);

  const applySnapshot = useCallback((snap: FormSnapshot) => {
    setFirstName(snap.firstName);
    setLastName(snap.lastName);
    setEmails(snap.emails);
    setPhones(snap.phones);
    setCompany(snap.company);
    setTitle(snap.title);
    setNotes(snap.notes);
    setSource(snap.source);
    setCategoryId(snap.categoryId);
    setPriorityId(snap.priorityId);
    setGatheredAt(snap.gatheredAt);
    setTags(snap.tags);
    setHistoryEntries(snap.historyEntries);
  }, []);

  function buildInitialSnapshot(c: ContactDetail | null): FormSnapshot {
    const storedHistory = c?.metadata?.contactHistory;
    let entries: HistoryEntry[];
    if (Array.isArray(storedHistory)) {
      entries = storedHistory as HistoryEntry[];
    } else {
      const legacy = c?.metadata?.interactionsLog as string | undefined;
      entries = legacy ? [{ id: crypto.randomUUID(), text: legacy, date: c!.createdAt }] : [];
    }
    return {
      firstName: c?.firstName || '',
      lastName: c?.lastName || '',
      emails: c?.emails || [],
      phones: c?.phones || [],
      company: c?.company || '',
      title: c?.title || '',
      notes: c?.notes || '',
      source: c?.source || '',
      categoryId: c?.categoryId || 'none',
      priorityId: c?.priorityId || 'none',
      gatheredAt: c?.gatheredAt
        ? new Date(c.gatheredAt).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      tags: c?.tags || [],
      historyEntries: entries,
    };
  }

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const snap = buildInitialSnapshot(contact);
      applySnapshot(snap);
      setInitialSnapshot(snap);
      setTagInput('');
      setNewHistoryText('');
    }
  }, [open, contact, applySnapshot]);

  const addHistoryEntry = useCallback(() => {
    if (!newHistoryText.trim()) return;
    setHistoryEntries((prev) => [
      {
        id: crypto.randomUUID(),
        text: newHistoryText.trim(),
        date: new Date().toISOString(),
        showDate: true,
      },
      ...prev,
    ]);
    setNewHistoryText('');
  }, [newHistoryText]);

  const removeHistoryEntry = useCallback((id: string) => {
    setHistoryEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const toggleEntryDate = useCallback((id: string) => {
    setHistoryEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, showDate: !(e.showDate ?? true) } : e)),
    );
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, overIdx: number) => {
      e.preventDefault();
      if (dragIdx === null || dragIdx === overIdx) return;
      setHistoryEntries((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragIdx, 1);
        next.splice(overIdx, 0, moved);
        return next;
      });
      setDragIdx(overIdx);
    },
    [dragIdx],
  );

  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && tagInput.trim()) {
        e.preventDefault();
        const newTag = tagInput.trim().toLowerCase();
        if (!tags.includes(newTag)) {
          setTags([...tags, newTag]);
        }
        setTagInput('');
      }
    },
    [tagInput, tags],
  );

  const removeTag = useCallback(
    (tag: string) => {
      setTags(tags.filter((t) => t !== tag));
    },
    [tags],
  );

  const buildSaveData = useCallback(
    (): ContactInput => ({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      emails: emails
        .filter((e) => e.value.trim())
        .map((e) => ({ value: e.value.trim(), label: e.label.trim() })),
      phones: phones
        .filter((p) => p.value.trim())
        .map((p) => ({ value: p.value.trim(), label: p.label.trim() })),
      company: company.trim() || null,
      title: title.trim() || null,
      notes: notes.trim() || null,
      source: source.trim() || null,
      categoryId: categoryId === 'none' ? null : categoryId,
      priorityId: priorityId === 'none' ? null : priorityId,
      gatheredAt: gatheredAt || null,
      tags,
      metadata: { contactHistory: historyEntries.length > 0 ? historyEntries : null },
    }),
    [
      firstName,
      lastName,
      emails,
      phones,
      company,
      title,
      notes,
      source,
      categoryId,
      priorityId,
      gatheredAt,
      tags,
      historyEntries,
    ],
  );

  // Auto-save when closing an existing contact
  const handleClose = useCallback(async () => {
    if (contact && hasChanges && (firstName.trim() || lastName.trim())) {
      setSaving(true);
      try {
        await onSave(buildSaveData(), contact.id);
      } catch {
        // Error handled by parent
      } finally {
        setSaving(false);
      }
    }
    onOpenChange(false);
  }, [contact, hasChanges, firstName, lastName, buildSaveData, onSave, onOpenChange]);

  // For new contacts, use the form submit flow
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!firstName.trim() && !lastName.trim()) {
        toast({ title: 'First or last name is required', variant: 'destructive' });
        return;
      }

      setSaving(true);
      try {
        await onSave(buildSaveData(), contact?.id);
        onOpenChange(false);
      } catch {
        // Error handled by parent
      } finally {
        setSaving(false);
      }
    },
    [firstName, lastName, contact, buildSaveData, onSave, onOpenChange],
  );

  const handleRevert = useCallback(() => {
    if (initialSnapshot) {
      applySnapshot(initialSnapshot);
    }
  }, [initialSnapshot, applySnapshot]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent
        className={`${contact ? 'max-w-4xl pt-12' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}
        onOpenAutoFocus={contact ? (e) => e.preventDefault() : undefined}
        hideClose={!!contact}
      >
        <DialogHeader>
          {contact ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
              >
                <ChevronLeft className="h-7 w-7" />
                <span className="sr-only">Close</span>
              </button>
              <div className="border-b pb-3 flex gap-2">
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="text-lg font-semibold border-muted flex-1"
                />
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="text-lg font-semibold border-muted flex-1"
                />
              </div>
              <DialogDescription>Update contact information.</DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle>Add Contact</DialogTitle>
              <DialogDescription>Add a new contact to your book.</DialogDescription>
            </>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className={contact ? 'flex gap-6' : ''}>
            {/* Left side: contact fields */}
            <div className={`space-y-4 ${contact ? 'flex-1 min-w-0' : ''}`}>
              {!contact && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-first-name">First Name *</Label>
                    <Input
                      id="contact-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-last-name">Last Name</Label>
                    <Input
                      id="contact-last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <LabeledEntryList
                  entries={emails}
                  onChange={setEmails}
                  labels={EMAIL_LABELS}
                  defaultLabel="Work"
                  placeholder="email@example.com"
                  fieldLabel="Emails"
                  inputType="email"
                />
                <LabeledEntryList
                  entries={phones}
                  onChange={setPhones}
                  labels={PHONE_LABELS}
                  defaultLabel="Mobile"
                  placeholder="+1 (555) 123-4567"
                  fieldLabel="Phones"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-company">Company</Label>
                  <Input
                    id="contact-company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-title">Title</Label>
                  <Input
                    id="contact-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Job title"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-category">Category</Label>
                  {creatingCategory ? (
                    <div className="space-y-2 rounded-md border p-2">
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Category name"
                        autoFocus
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {PRESET_COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setNewCategoryColor(c)}
                            className={`h-5 w-5 rounded-full border-2 transition-all ${
                              newCategoryColor === c
                                ? 'border-foreground scale-110'
                                : 'border-transparent'
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setCreatingCategory(false);
                            setNewCategoryName('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="flex-1"
                          disabled={!newCategoryName.trim() || savingCategory}
                          onClick={async () => {
                            setSavingCategory(true);
                            try {
                              const created = await onCreateCategory({
                                name: newCategoryName.trim(),
                                color: newCategoryColor,
                              });
                              setCategoryId(created.id);
                              setCreatingCategory(false);
                              setNewCategoryName('');
                            } catch {
                              // Error handled by parent
                            } finally {
                              setSavingCategory(false);
                            }
                          }}
                        >
                          {savingCategory ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Create'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Select
                      value={categoryId}
                      onValueChange={(val) => {
                        if (val === '__new__') {
                          setCreatingCategory(true);
                        } else {
                          setCategoryId(val);
                        }
                      }}
                    >
                      <SelectTrigger id="contact-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Uncategorized</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span
                                className="h-2 w-2 rounded-full inline-block"
                                style={{ backgroundColor: cat.color }}
                              />
                              {cat.name}
                            </span>
                          </SelectItem>
                        ))}
                        <SelectItem value="__new__">
                          <span className="flex items-center gap-2 text-primary">
                            <Plus className="h-3 w-3" />
                            New Category
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-source">Source</Label>
                  <Input
                    id="contact-source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder="e.g., Referral, Conference"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-priority">Priority</Label>
                  {creatingPriority ? (
                    <div className="space-y-2 rounded-md border p-2">
                      <Input
                        value={newPriorityName}
                        onChange={(e) => setNewPriorityName(e.target.value)}
                        placeholder="Priority name"
                        autoFocus
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {PRESET_COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setNewPriorityColor(c)}
                            className={`h-5 w-5 rounded-full border-2 transition-all ${
                              newPriorityColor === c
                                ? 'border-foreground scale-110'
                                : 'border-transparent'
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setCreatingPriority(false);
                            setNewPriorityName('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="flex-1"
                          disabled={!newPriorityName.trim() || savingPriority}
                          onClick={async () => {
                            setSavingPriority(true);
                            try {
                              const created = await onCreatePriority({
                                name: newPriorityName.trim(),
                                color: newPriorityColor,
                              });
                              setPriorityId(created.id);
                              setCreatingPriority(false);
                              setNewPriorityName('');
                            } catch {
                              // Error handled by parent
                            } finally {
                              setSavingPriority(false);
                            }
                          }}
                        >
                          {savingPriority ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Create'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Select
                      value={priorityId}
                      onValueChange={(val) => {
                        if (val === '__new__') {
                          setCreatingPriority(true);
                        } else {
                          setPriorityId(val);
                        }
                      }}
                    >
                      <SelectTrigger id="contact-priority">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Priority</SelectItem>
                        {priorities.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            <span className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full inline-block"
                                style={{ backgroundColor: p.color }}
                              />
                              {p.name}
                            </span>
                          </SelectItem>
                        ))}
                        <SelectItem value="__new__">
                          <span className="flex items-center gap-2 text-primary">
                            <Plus className="h-3 w-3" />
                            New Priority
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-gathered">Date Gathered</Label>
                  <Input
                    id="contact-gathered"
                    type="date"
                    value={gatheredAt}
                    onChange={(e) => setGatheredAt(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-tags">Tags</Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <Input
                  id="contact-tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Type a tag and press Enter"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-notes">Notes</Label>
                <Textarea
                  id="contact-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes about this contact..."
                  rows={3}
                />
              </div>

              {/* Attachments (edit mode only) */}
              {contact && <ContactAttachments contactId={contact.id} />}
            </div>

            {/* Right side: Contact History (edit mode only) */}
            {contact && (
              <>
                <div className="w-px bg-border flex-shrink-0" />
                <div className="flex-1 min-w-0 flex flex-col">
                  <Label className="text-base font-semibold mb-3">Contact History</Label>

                  <div className="flex gap-2 mb-3">
                    <Input
                      value={newHistoryText}
                      onChange={(e) => setNewHistoryText(e.target.value)}
                      placeholder="Log a call, meeting, note..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addHistoryEntry();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!newHistoryText.trim()}
                      onClick={addHistoryEntry}
                    >
                      Add
                    </Button>
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-2 max-h-[50vh]">
                    {historyEntries.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No history entries yet. Add your first interaction above.
                      </p>
                    ) : (
                      historyEntries.map((entry, idx) => (
                        <div
                          key={entry.id}
                          draggable
                          onDragStart={() => setDragIdx(idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDragEnd={() => setDragIdx(null)}
                          className={`group/entry flex items-start gap-1.5 rounded-md border p-2.5 transition-opacity ${
                            dragIdx === idx ? 'opacity-50' : ''
                          }`}
                        >
                          <div
                            className="flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground mt-0.5"
                            title="Drag to reorder"
                          >
                            <GripVertical className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">{entry.text}</p>
                            <div className="flex items-center gap-1 mt-1">
                              {(entry.showDate ?? true) ? (
                                <p className="text-xs text-muted-foreground">
                                  {new Date(entry.date).toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">Date hidden</p>
                              )}
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-5 w-5 text-muted-foreground opacity-0 group-hover/entry:opacity-100 transition-opacity"
                                onClick={() => toggleEntryDate(entry.id)}
                                title={(entry.showDate ?? true) ? 'Hide date' : 'Show date'}
                              >
                                {(entry.showDate ?? true) ? (
                                  <EyeOff className="h-3 w-3" />
                                ) : (
                                  <Clock className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 flex-shrink-0 opacity-0 group-hover/entry:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                            onClick={() => removeHistoryEntry(entry.id)}
                            title="Delete entry"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="mt-4">
            {contact ? (
              <>
                {hasChanges && (
                  <Button type="button" variant="outline" onClick={handleRevert}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Revert Changes
                  </Button>
                )}
                {saving && (
                  <span className="flex items-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Saving...
                  </span>
                )}
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Add Contact
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
